import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const REPLACEMENTS = [
  [/64\/085,045/g, '64/084,045'],
  [/Public-safe engineering white paper • 29 pages/g, 'Public-safe executive brief • 4 pages'],
  [/https:\/\/res\.cloudinary\.com\/dp5hfqkdu\/raw\/upload\/v1784774137\/salar-publications\/SALAR_CBAE_Public_Safe_White_Paper_v1_0\.pdf/g, '/api/cbae-public-safe-brief'],
  [/https:\/\/res\.cloudinary\.com\/dp5hfqkdu\/raw\/upload\/v\d+\/SALAR_CBAE_Open_Weight_Public_Safe_White_Paper_v1_1_8pp(?:\.pdf)?/g, '/api/open-weights-public-brief'],
  [/5edf4285d06978882aa75fe0c725f0d844d99324a53d35abb3579c20fbb21cc3/g, '7cf7d03082cfb7c6602932e41cde59194c7a619826d243fe5ab7277d65a8603e'],
  [/Eight USPTO patent filings/g, 'Eleven USPTO patent filings'],
  [/eight USPTO patent filings/g, 'eleven USPTO patent filings'],
  [/Nine USPTO patent filings/g, 'Eleven USPTO patent filings'],
  [/nine USPTO patent filings/g, 'eleven USPTO patent filings'],
  [/9 patent filings/g, '11 patent filings'],
  [/Eight public USPTO patent filings/g, 'Eleven public patent filings'],
  [/eight public USPTO patent filings/g, 'eleven public patent filings'],
  [/Nine public patent filings/g, 'Eleven public patent filings'],
  [/nine public patent filings/g, 'eleven public patent filings'],
  [/Eight pending US patent applications/g, 'Ten pending U.S. patent filings'],
  [/eight pending US patent applications/g, 'ten pending U.S. patent filings'],
  [/Nine pending U\.S\. patent filings/g, 'Ten pending U.S. patent filings'],
  [/nine pending U\.S\. patent filings/g, 'ten pending U.S. patent filings'],
  [/eight pending US utility patent applications/g, 'ten pending U.S. patent filings'],
  [/Eight Patent Areas/g, 'Ten Patent Areas'],
  [/Eight patent areas/g, 'Eleven patent areas'],
  [/eight patent areas/g, 'eleven patent areas'],
  [/Nine Patent Areas/g, 'Ten Patent Areas'],
  [/Nine patent areas/g, 'Eleven patent areas'],
  [/nine patent areas/g, 'eleven patent areas'],
  [/Seven patent areas/g, 'Eleven patent areas'],
  [/seven patent areas/g, 'eleven patent areas'],
  [/7 Patent Areas/g, '11 Patent Areas'],
  [/8 Patent Areas/g, '11 Patent Areas'],
  [/9 Patent Areas/g, '11 Patent Areas'],
  [/7 Pending/g, '10 Pending'],
  [/8 Pending/g, '10 Pending'],
  [/9 Pending/g, '10 Pending'],
  [/Eight patent-pending technologies/g, 'Ten patent-pending technologies'],
  [/eight patent-pending technologies/g, 'ten patent-pending technologies'],
  [/Nine patent-pending technologies/g, 'Ten patent-pending technologies'],
  [/nine patent-pending technologies/g, 'ten patent-pending technologies'],
  [/Eight public patent-pending application areas/g, 'Ten public patent-pending application areas'],
  [/eight public patent-pending application areas/g, 'ten public patent-pending application areas'],
  [/Nine public patent-pending application areas/g, 'Ten public patent-pending application areas'],
  [/nine public patent-pending application areas/g, 'ten public patent-pending application areas'],
  [/All 8 USPTO applications/g, 'All 10 USPTO patent filings'],
  [/all 8 USPTO applications/g, 'all 10 USPTO patent filings'],
  [/All 9 USPTO patent filings/g, 'All 10 USPTO patent filings'],
  [/all 9 USPTO patent filings/g, 'all 10 USPTO patent filings'],
  [/8 USPTO applications/g, '10 USPTO patent filings'],
  [/9 USPTO patent filings/g, '10 USPTO patent filings'],
  [/Full Portfolio \(all 7\)/g, 'Full Portfolio (all 10)'],
  [/Full Portfolio \(all 8\)/g, 'Full Portfolio (all 10)'],
  [/Full Portfolio \(all 9\)/g, 'Full Portfolio (all 10)'],
  [/P1–P8/g, 'P1–P10'],
  [/P1-P8/g, 'P1-P10'],
];

const ATTRIBUTE_NAMES = ['content', 'title', 'aria-label', 'alt', 'placeholder', 'href'];

function applyReplacements(value) {
  if (!value) return value;
  let updated = value;
  for (const [pattern, replacement] of REPLACEMENTS) {
    updated = updated.replace(pattern, replacement);
  }
  return updated;
}

function shouldSkipNode(node) {
  const parent = node.parentElement;
  if (!parent) return true;
  const tag = parent.tagName?.toLowerCase();
  return ['script', 'style', 'textarea', 'input', 'noscript'].includes(tag);
}

function replacePatentText() {
  if (typeof document === 'undefined' || !document.body) return;
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
  let node;
  while ((node = walker.nextNode())) {
    if (shouldSkipNode(node)) continue;
    const original = node.nodeValue || '';
    const updated = applyReplacements(original);
    if (updated !== original) node.nodeValue = updated;
  }
}

function replaceMetadataAndAttributes() {
  if (typeof document === 'undefined') return;
  document.title = applyReplacements(document.title);
  ATTRIBUTE_NAMES.forEach((attributeName) => {
    document.querySelectorAll(`[${attributeName}]`).forEach((el) => {
      const original = el.getAttribute(attributeName);
      const updated = applyReplacements(original);
      if (updated !== original) el.setAttribute(attributeName, updated);
    });
  });
}

function patchLicensingStack() {
  if (typeof document === 'undefined') return;
  const section = document.querySelector('[data-testid="licensing-stack"]');
  if (!section || section.querySelector('[data-salar-patent10-stack="true"]')) return;
  const stack = section.querySelector('.space-y-2');
  if (!stack) return;
  const rows = [
    ['10', 'Fault-Tolerance-Indexed Quantum Hardware Assurance', '/licensing/patent-10-fault-tolerance-indexed-hardware-assurance', 'data-salar-patent10-stack'],
    ['9', 'High-Speed Validation / Instrument Alignment', '/licensing/patent-9-high-speed-validation-alignment', 'data-salar-patent9-stack'],
  ];
  rows.reverse().forEach(([num, label, href, attr]) => {
    if (stack.querySelector(`[${attr}="true"]`)) return;
    const a = document.createElement('a');
    a.href = href;
    a.setAttribute(attr, 'true');
    a.className = 'group block';
    a.innerHTML = `<div class="relative flex items-center gap-4 rounded-md border border-cyan-400/40 bg-blue-950/60 px-4 py-3 sm:px-6 sm:py-4 transition-all hover:border-cyan-400/70 hover:bg-blue-950/80 hover:translate-x-1"><div class="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-md bg-cyan-300 text-[#0a1628] font-bold flex items-center justify-center text-base sm:text-lg shadow-md">P${num}</div><div class="flex-1 min-w-0"><p class="font-semibold leading-tight text-white text-sm sm:text-base">${label}</p><p class="text-[11px] uppercase tracking-widest text-cyan-300 mt-1">Filed - Patent Pending</p></div><span class="flex-shrink-0 text-blue-300 group-hover:text-cyan-300 transition-colors">→</span></div>`;
    stack.prepend(a);
  });
}

function runCleanup() {
  replacePatentText();
  replaceMetadataAndAttributes();
  patchLicensingStack();
}

export default function Patent9SiteTextCleanup() {
  const location = useLocation();

  useEffect(() => {
    const run = () => runCleanup();
    const animationFrame = window.requestAnimationFrame(run);
    const observer = new MutationObserver(run);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ATTRIBUTE_NAMES,
    });

    return () => {
      window.cancelAnimationFrame(animationFrame);
      observer.disconnect();
    };
  }, [location.pathname]);

  return null;
}
